import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import {
  onAuthStateChanged,
  signInWithPopup,
  signOut as fbSignOut,
  type User as FirebaseUser,
} from 'firebase/auth';
import { doc, getDoc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { auth, db, googleProvider } from '../services/firebase';
import { Sentry } from '../services/sentry';
import type { User } from '../types';
import {
  attachReferralCode,
  clearPendingReferral,
  getPendingReferral,
  normaliseCode,
  setPendingReferral,
} from '../services/referralsService';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
  /** Optimistic local increment; the source of truth is the Firestore doc. */
  addCredits: (n: number, plan?: User['plan']) => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * AuthProvider wires the React tree to Firebase Auth + a Firestore user profile.
 *
 * Flow:
 *   1. onAuthStateChanged fires with a FirebaseUser (or null).
 *   2. We ensure users/{uid} exists in Firestore — first sign-in gets the free
 *      plan + 1 credit bootstrap.
 *   3. onSnapshot streams the profile doc so credit/plan changes from Stripe
 *      webhooks show up without a manual refresh.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Capture `?ref=CODE` from the URL as soon as the app boots. We stash it in
  // sessionStorage rather than attaching it immediately because the user may
  // not be signed in yet — the attach happens after auth resolves below.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const incoming = normaliseCode(params.get('ref'));
      if (incoming) setPendingReferral(incoming);
    } catch { /* URL parsing shouldn't ever throw here, but don't block boot */ }
  }, []);

  useEffect(() => {
    let profileUnsub: (() => void) | undefined;

    const authUnsub = onAuthStateChanged(auth, async (fbUser) => {
      // Drop any in-flight profile listener when the auth state changes.
      profileUnsub?.();

      if (!fbUser) {
        setUser(null);
        setLoading(false);
        Sentry.setUser(null); // drop PII from Sentry scope on sign-out
        return;
      }

      try {
        const profile = await ensureUserDoc(fbUser);
        setUser(profile);
        // Tag Sentry scope so prod errors are traceable to a user (no email —
        // Firebase UID only, which is safer if someone leaks a screenshot).
        Sentry.setUser({ id: fbUser.uid });

        // Attempt to attach a pending referral code (from a `?ref=` URL) now
        // that we have an authenticated session. Swallow errors — an invalid
        // code shouldn't break sign-in, and the backend is idempotent so a
        // race with concurrent tabs is fine.
        const pendingRef = getPendingReferral();
        if (pendingRef && !profile.referredBy) {
          try {
            await attachReferralCode(pendingRef);
            clearPendingReferral();
          } catch (err) {
            console.warn('[referral] attach failed', err);
            // Leave the pending code in place only if the error was network-ish;
            // for definite rejections (404/409/403) clear so we don't retry.
            const status = (err as any)?.response?.status;
            if (status && status !== 0 && status < 500) clearPendingReferral();
          }
        }

        // Live-sync subsequent changes (credit top-ups, plan changes).
        profileUnsub = onSnapshot(doc(db, 'users', fbUser.uid), (snap) => {
          if (!snap.exists()) return;
          const data = snap.data() as Partial<User>;
          setUser((prev) => ({
            ...(prev ?? firebaseUserToShell(fbUser)),
            ...data,
          }));
        });
      } catch (err) {
        console.error('Failed to load user profile', err);
        setUser(firebaseUserToShell(fbUser));
      } finally {
        setLoading(false);
      }
    });

    return () => {
      authUnsub();
      profileUnsub?.();
    };
  }, []);

  const signInWithGoogle = useCallback(async () => {
    await signInWithPopup(auth, googleProvider);
  }, []);

  const signOut = useCallback(async () => {
    await fbSignOut(auth);
  }, []);

  const refresh = useCallback(async () => {
    if (!auth.currentUser) return;
    const snap = await getDoc(doc(db, 'users', auth.currentUser.uid));
    if (snap.exists()) setUser((prev) => ({ ...(prev ?? firebaseUserToShell(auth.currentUser!)), ...(snap.data() as Partial<User>) }));
  }, []);

  const addCredits = useCallback((n: number, plan?: User['plan']) => {
    // Optimistic local update only. The authoritative credit balance lives on
    // users/{uid}.credits and is written exclusively by the backend via the
    // Admin SDK (Stripe webhook for top-ups, monthlyQuota middleware for
    // decrements). Firestore security rules reject any client write to the
    // `credits` field, so attempting updateDoc here would silently fail.
    // The onSnapshot listener in the AuthProvider effect syncs the real value
    // back to the UI as soon as the server write lands.
    setUser((prev) => (prev ? { ...prev, credits: prev.credits + n, ...(plan ? { plan } : {}) } : prev));
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, signInWithGoogle, signOut, refresh, addCredits }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/**
 * Ensure `users/{uid}` exists. First-ever sign-in bootstraps the profile with
 * the free plan + 1 free evaluation credit. Returns the merged User object.
 */
async function ensureUserDoc(fbUser: FirebaseUser): Promise<User> {
  const ref = doc(db, 'users', fbUser.uid);
  const snap = await getDoc(ref);

  if (!snap.exists()) {
    // Only include avatarUrl if Google actually gave us one. Firestore rejects
    // `undefined` field values by default; we also set ignoreUndefinedProperties
    // at initialization, but keeping the payload clean avoids surprises if the
    // flag is ever removed.
    const bootstrap: Record<string, unknown> = {
      email: fbUser.email ?? '',
      name: fbUser.displayName ?? (fbUser.email?.split('@')[0] ?? 'User'),
      credits: 1,
      plan: 'free' as const,
      provider: providerOf(fbUser),
      createdAt: serverTimestamp(),
      monthlyOpsUsed: 0,
    };
    if (fbUser.photoURL) bootstrap.avatarUrl = fbUser.photoURL;
    await setDoc(ref, bootstrap);
    return {
      id: fbUser.uid,
      email: fbUser.email ?? '',
      name: (bootstrap.name as string) ?? 'User',
      avatarUrl: fbUser.photoURL ?? undefined,
      credits: 1,
      plan: 'free',
      provider: providerOf(fbUser),
    };
  }

  const data = snap.data();
  return {
    id: fbUser.uid,
    email: data.email ?? fbUser.email ?? '',
    name: data.name ?? fbUser.displayName ?? 'User',
    avatarUrl: data.avatarUrl ?? fbUser.photoURL ?? undefined,
    credits: data.credits ?? 0,
    plan: data.plan ?? 'free',
    provider: data.provider ?? providerOf(fbUser),
    // Copy referral state forward so the post-signup attach guard
    // ("if not already referred") sees the real server value on reload,
    // not undefined.
    referralCode: data.referralCode ?? undefined,
    referredBy: data.referredBy ?? null,
    referralsRewarded: data.referralsRewarded ?? 0,
    referralBonusAwarded: Boolean(data.referralBonusAwarded),
    phoneVerified: Boolean(data.phoneVerified),
  };
}

function providerOf(fbUser: FirebaseUser): User['provider'] {
  const pid = fbUser.providerData[0]?.providerId;
  if (pid === 'google.com') return 'google';
  return 'google';
}

function firebaseUserToShell(fbUser: FirebaseUser): User {
  return {
    id: fbUser.uid,
    email: fbUser.email ?? '',
    name: fbUser.displayName ?? 'User',
    avatarUrl: fbUser.photoURL ?? undefined,
    credits: 0,
    plan: 'free',
    provider: providerOf(fbUser),
  };
}
